// マイクの MediaStream から 16kHz mono の PCM を取り続ける (spec 2026-08-29 §3.2)。
//
// cascade は Silero VAD が発話区間を切り出して渡すが、**realtime は server 側が終端を
// 判定する**ので音声を常に送り続ける必要がある。その結果 Silero VAD 自体が要らなくなり、
// onnxruntime-web の WebAssembly SIMD 依存も外れる (spec D14)。
//
// **16kHz で開くのが要点** — cascade と同じ形式なので R2 の WAV が揃い
// (docs/research/name-accuracy/*.py が 16000 を assert している)、送信時の 24kHz 化も
// 既存の upsample16kTo24k がそのまま使える。
//
// ScriptProcessorNode は仕様上は非推奨だが、AudioWorklet は別ファイルの読み込みが要り
// Vite のビルド設定に手が入る。差し替えるときはこのクラスの中だけで閉じる。

const RATE = 16000;
const BUFFER_SIZE = 4096; // 16kHz で 256ms

export class MicPcmSource {
  private ctx: AudioContext | null = null;
  private node: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private running = false;

  start(stream: MediaStream, onPcm: (pcm: Float32Array) => void): void {
    this.stop(); // 二重 start で前のものを孤児にしない
    try {
      const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
      if (!Ctor) return;
      const ctx = new Ctor({ sampleRate: RATE });
      const source = ctx.createMediaStreamSource(stream);
      const node = ctx.createScriptProcessor(BUFFER_SIZE, 1, 1);
      node.onaudioprocess = (e) => {
        if (!this.running) return;
        // **コピーして渡す** — getChannelData が返す配列は次の呼び出しで中身が
        // 書き換わるので、参照のまま渡すと送信キューの音声が後から化ける。
        onPcm(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      source.connect(node);
      node.connect(ctx.destination);
      this.ctx = ctx;
      this.node = node;
      this.source = source;
      this.running = true;
    } catch {
      // マイクを開けなくても本編は続ける (原則 5)。呼び出し側が別経路へ倒す。
      this.running = false;
    }
  }

  stop(): void {
    this.running = false;
    try {
      this.node?.disconnect();
      this.source?.disconnect();
      void this.ctx?.close();
    } catch {
      // 既に閉じている
    }
    this.node = null;
    this.source = null;
    this.ctx = null;
  }
}
